/**
 * Billing Settings Tab — Org-facing billing view in Settings.
 * Wires to GET /billing/me to show current subscription, seat estimate,
 * and invoice history. Replaces the mock LicenseSettingsTab.
 */

import { useQuery } from '@tanstack/react-query';
import styled from 'styled-components';
import { AlertCircle, FileText, CreditCard } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Card } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import billingService from '../../services/api/billingService';
import { formatLocalDate } from '../../utils/dateUtils';
import { formatAmount, SUBSCRIPTION_STATUS_VARIANT, INVOICE_STATUS_VARIANT } from '../../utils/billingUtils';
import type { Invoice } from '@upskill/shared';

// ============================================================================
// Helpers
// ============================================================================


// ============================================================================
// Component
// ============================================================================

export const BillingSettingsTab = () => {
  const { t } = useTranslation();
  const { data, isLoading, isError } = useQuery({
    queryKey: ['billing-me'],
    queryFn: () => billingService.getMyBilling(),
    staleTime: 2 * 60_000,
  });

  if (isLoading) {
    return <LoadingState>{t('billing.billingSettings.loadingBilling')}</LoadingState>;
  }

  if (isError) {
    return (
      <ErrorState>
        <AlertCircle size={16} />
        {t('billing.billingSettings.errorBilling')}
      </ErrorState>
    );
  }

  const { subscription, currentSeatEstimate, invoices } = data ?? {
    subscription: null,
    currentSeatEstimate: 0,
    invoices: [],
  };

  return (
    <Section>
      {/* Current Subscription */}
      <ContentCard padding="lg">
        <CardTitle>
          <CreditCard size={16} />
          {t('billing.billingSettings.currentSubscriptionTitle')}
        </CardTitle>

        {subscription ? (
          <>
            <SubscriptionGrid>
              <InfoItem>
                <InfoLabel>{t('billing.billingSettings.labelPlan')}</InfoLabel>
                <InfoValue>{subscription.plan?.name ?? subscription.planId}</InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>{t('billing.billingSettings.labelStatus')}</InfoLabel>
                <InfoValue>
                  <Badge variant={SUBSCRIPTION_STATUS_VARIANT[subscription.status]}>
                    {t(`billing.status.subscription.${subscription.status}`)}
                  </Badge>
                </InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>{t('billing.billingSettings.labelBillingCadence')}</InfoLabel>
                <InfoValue>{t('billing.billingSettings.billingCadenceMonthly')}</InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>{t('billing.billingSettings.labelCurrentPeriod')}</InfoLabel>
                <InfoValue>
                  {formatLocalDate(subscription.currentPeriodStart)} –{' '}
                  {formatLocalDate(subscription.currentPeriodEnd)}
                </InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>{t('billing.billingSettings.labelBillableSeats')}</InfoLabel>
                <InfoValue $highlight>{currentSeatEstimate}</InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>{t('billing.billingSettings.labelStartDate')}</InfoLabel>
                <InfoValue>{formatLocalDate(subscription.startDate)}</InfoValue>
              </InfoItem>
            </SubscriptionGrid>

            {subscription.status === 'PAST_DUE' && (
              <StatusBanner $variant="warning">
                <AlertCircle size={14} />
                {t('billing.billingSettings.pastDueBanner')}
              </StatusBanner>
            )}
            {subscription.status === 'CANCELED' && (
              <StatusBanner $variant="error">
                <AlertCircle size={14} />
                {t('billing.billingSettings.canceledBanner')}
              </StatusBanner>
            )}
          </>
        ) : (
          <EmptySubscription>
            <AlertCircle size={24} />
            <p>{t('billing.billingSettings.noSubscription')}</p>
            <p>{t('billing.billingSettings.noSubscriptionContact')}</p>
          </EmptySubscription>
        )}
      </ContentCard>

      {/* Invoice History */}
      <ContentCard padding="lg">
        <CardTitle>
          <FileText size={16} />
          {t('billing.billingSettings.invoiceHistoryTitle')}
        </CardTitle>

        {invoices.length === 0 ? (
          <EmptyInvoices>{t('billing.billingSettings.noInvoices')}</EmptyInvoices>
        ) : (
          <InvoiceTable>
            <InvoiceHead>
              <tr>
                <ITh>{t('billing.billingSettings.colPeriod')}</ITh>
                <ITh $right>{t('billing.billingSettings.colAmount')}</ITh>
                <ITh $center>{t('billing.billingSettings.colSeats')}</ITh>
                <ITh $center>{t('billing.billingSettings.colStatus')}</ITh>
                <ITh>{t('billing.billingSettings.colIssued')}</ITh>
              </tr>
            </InvoiceHead>
            <tbody>
              {invoices.map((inv: Invoice) => {
                const statusVariant = INVOICE_STATUS_VARIANT[inv.status];
                const statusLabel = t(`billing.status.invoice.${inv.status}`);
                return (
                  <ITr key={inv.id}>
                    <ITd>
                      {formatLocalDate(inv.periodStart)} – {formatLocalDate(inv.periodEnd)}
                    </ITd>
                    <ITd $right>
                      <AmountText>{formatAmount(inv.amount, inv.currency)}</AmountText>
                    </ITd>
                    <ITd $center>{inv.paidSeats}</ITd>
                    <ITd $center>
                      <Badge variant={statusVariant}>{statusLabel}</Badge>
                    </ITd>
                    <ITd>{formatLocalDate(inv.issuedAt)}</ITd>
                  </ITr>
                );
              })}
            </tbody>
          </InvoiceTable>
        )}
      </ContentCard>
    </Section>
  );
};

export default BillingSettingsTab;

// ============================================================================
// Styled Components
// ============================================================================

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[5]};
`;

const ContentCard = styled(Card)``;

const CardTitle = styled.h3`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0 0 ${({ theme }) => theme.spacing[5]};
  padding-bottom: ${({ theme }) => theme.spacing[4]};
  border-bottom: 1px solid ${({ theme }) => theme.colors.borders.light};

  svg {
    color: ${({ theme }) => theme.colors.text.secondary};
    flex-shrink: 0;
  }
`;

const SubscriptionGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }) => theme.spacing[4]};

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
  }
`;

const InfoItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[1]};
`;

const InfoLabel = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.light};
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const InfoValue = styled.span<{ $highlight?: boolean }>`
  font-size: ${({ $highlight, theme }) =>
    $highlight ? theme.typography.fontSize['2xl'] : theme.typography.fontSize.sm};
  font-weight: ${({ $highlight, theme }) =>
    $highlight
      ? theme.typography.fontWeight.bold
      : theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const StatusBanner = styled.div<{ $variant: 'warning' | 'error' }>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  margin-top: ${({ theme }) => theme.spacing[4]};
  padding: ${({ theme }) => theme.spacing[3]};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};

  ${({ $variant, theme }) =>
    $variant === 'warning'
      ? `
    background: ${theme.colors.status.warning.bg};
    border: 1px solid ${theme.colors.status.warning.border};
    color: ${theme.colors.status.warning.text};
  `
      : `
    background: ${theme.colors.status.error.bg};
    border: 1px solid ${theme.colors.status.error.border};
    color: ${theme.colors.status.error.text};
  `}

  svg {
    flex-shrink: 0;
  }
`;

const EmptySubscription = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  padding: ${({ theme }) => theme.spacing[8]} 0;
  text-align: center;
  color: ${({ theme }) => theme.colors.text.secondary};

  svg {
    color: ${({ theme }) => theme.colors.text.light};
  }

  p {
    margin: 0;
    font-size: ${({ theme }) => theme.typography.fontSize.sm};
  }
`;

const EmptyInvoices = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.secondary};
  margin: 0;
  padding: ${({ theme }) => theme.spacing[4]} 0;
`;

const InvoiceTable = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const InvoiceHead = styled.thead`
  background: ${({ theme }) => theme.colors.backgrounds.gray};
`;

const ITh = styled.th<{ $right?: boolean; $center?: boolean }>`
  padding: ${({ theme }) => theme.spacing[2]} ${({ theme }) => theme.spacing[3]};
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.light};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  text-align: ${({ $right, $center }) => ($right ? 'right' : $center ? 'center' : 'left')};
  border-bottom: 1px solid ${({ theme }) => theme.colors.borders.light};
  white-space: nowrap;
`;

const ITr = styled.tr`
  &:not(:last-child) td {
    border-bottom: 1px solid ${({ theme }) => theme.colors.borders.light}40;
  }
`;

const ITd = styled.td<{ $right?: boolean; $center?: boolean }>`
  padding: ${({ theme }) => theme.spacing[3]};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.secondary};
  text-align: ${({ $right, $center }) => ($right ? 'right' : $center ? 'center' : 'left')};
  vertical-align: middle;
`;

const AmountText = styled.span`
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const LoadingState = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.secondary};
  padding: ${({ theme }) => theme.spacing[6]} 0;
`;

const ErrorState = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  padding: ${({ theme }) => theme.spacing[4]};
  background: ${({ theme }) => theme.colors.status.error.bg};
  border: 1px solid ${({ theme }) => theme.colors.status.error.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.status.error.text};

  svg {
    flex-shrink: 0;
  }
`;
